/**
 * readinessApi — frontend client for POST /readiness.
 *
 * Returns the uniform ReadinessResponse defined in ../types/readiness.
 * Hosts call this when they need the structured findings + status for
 * a given consumer; the response feeds directly into ReadinessPanel.
 */
import { apiClient, buildUrl } from './apiClient';
import type {
  ConsumerKind,
  ReadinessReportResponse,
  ReadinessResolutionRequest,
  ReadinessResolveResponse,
  ReadinessResponse,
} from '../types/readiness';

export interface FetchReadinessArgs {
  sessionId: string;
  consumerKind: ConsumerKind;
  /** Node id when relevant (e.g. a specific target_table). */
  nodeId?: string;
}

export async function fetchReadiness(
  args: FetchReadinessArgs,
): Promise<ReadinessResponse> {
  const url = buildUrl(
    `/api/v1/agents/sessions/${encodeURIComponent(args.sessionId)}/readiness`,
  );
  const response = await apiClient.post<ReadinessResponse>(url, {
    consumer_kind: args.consumerKind,
    ...(args.nodeId ? { node_id: args.nodeId } : {}),
  });
  return response.data;
}

// ─── Pre-mapping readiness (detector + resolutions) ──────────────────

export interface PreMappingReadinessRunArgs {
  batch_id: string;
  client_id: string;
  recipe_id: string;
  base_recipe?: Record<string, unknown>;
  profiles: Array<Record<string, unknown>>;
  target_schema?: Record<string, unknown> | null;
  metadata?: Record<string, unknown>;
}

/** POST /api/v1/mapping/readiness — runs the deterministic detector. */
export async function runPreMappingReadiness(
  args: PreMappingReadinessRunArgs,
): Promise<ReadinessReportResponse> {
  const url = buildUrl('/api/v1/mapping/readiness');
  const response = await apiClient.post<ReadinessReportResponse>(url, {
    base_recipe: args.base_recipe ?? {},
    ...args,
  });
  return response.data;
}

/**
 * POST /api/v1/mapping/readiness/for-batch/{batch_id} — convenience
 * endpoint used by the Generate Mapping dialog. Hydrates the full
 * PromptRunRequest from batch_store server-side so the UI only needs
 * the batch_id.
 *
 * ``sessionId`` is forwarded so the backend can publish live progress
 * events to the user's notifications SSE channel (consumed by the
 * dialog's PreMappingReadinessStep).
 */
export async function runPreMappingReadinessForBatch(
  batchId: string,
  options: { sessionId?: string; targetSchemaId?: string } = {},
): Promise<ReadinessReportResponse> {
  const url = buildUrl(
    `/api/v1/mapping/readiness/for-batch/${encodeURIComponent(batchId)}`,
  );
  const params: Record<string, string> = {};
  if (options.sessionId) params.session_id = options.sessionId;
  if (options.targetSchemaId) params.target_schema_id = options.targetSchemaId;
  // 5-minute override of the default 30s timeout: this call drives the
  // LLM-driven readiness agent (Phase 1 source summarisation + multi-
  // turn tool conversation). 30s is far too short — observed run is
  // ~60–90s for typical schemas, more for large ones. The backend
  // gateway is already configured for 300s on this route.
  const response = await apiClient.post<ReadinessReportResponse>(url, {}, {
    params: Object.keys(params).length > 0 ? params : undefined,
    timeout: 300_000,
  });
  return response.data;
}

/**
 * Upload a lookup/reference file and add it to the run's source
 * inventory. Reuses the existing session-scoped upload endpoint —
 * server returns the canonical ``table_name`` we then pass to
 * /readiness/resolve as the ``source_alias`` payload.
 */
export async function uploadLookupFile(
  sessionId: string,
  file: File,
): Promise<{ table_name: string; row_count: number }> {
  const url = buildUrl(
    `/api/v1/agents/sessions/${encodeURIComponent(sessionId)}/upload`,
  );
  const form = new FormData();
  form.append('file', file);
  const response = await apiClient.post<{ table_name: string; row_count: number }>(
    url,
    form,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return response.data;
}

/** POST /api/v1/mapping/readiness/resolve — apply operator answers. */
export async function resolvePreMappingFindings(
  batchId: string,
  resolutions: ReadinessResolutionRequest[],
): Promise<ReadinessResolveResponse> {
  const url = buildUrl('/api/v1/mapping/readiness/resolve');
  const response = await apiClient.post<ReadinessResolveResponse>(url, {
    batch_id: batchId,
    resolutions,
  });
  return response.data;
}

/** Response shape for post-mapping resolve endpoint. Distinct from the
 *  pre-mapping shape because the lifecycle differs (workspace_meta vs
 *  pre-mapping run config). */
export interface PostMappingResolveResponse {
  session_id: string;
  mapping_mode: 'engine' | 'iterative' | string;
  resolved_finding_ids: string[];
  errors: Array<{ finding_id?: string; reason: string }>;
  applied: {
    target_constants: Record<string, string>;
    skip_columns: string[];
    agent_hints_count: number;
    source_picks: Record<string, { source_table: string; source_column: string }>;
  };
  /**
   * Background actions the server scheduled in response to this
   * submit. Keys: ``materialize_mapping`` (engine mode, recipe-mutating
   * kinds), ``agent_turn`` (iterative mode, any actionable kind).
   * Values are the dispatch status (currently ``"scheduled"``). The UI
   * surfaces this in the toast so the operator knows what's happening
   * downstream instead of seeing the panel "not change".
   */
  dispatched?: Record<string, string>;
  next_step: string;
}

/** POST /api/v1/agents/sessions/{session_id}/readiness/resolve — apply
 *  operator resolutions to post-mapping readiness findings. Mirrors the
 *  pre-mapping resolve API shape but writes to workspace_meta (engine
 *  mode) + injects agent hints (iterative mode). The UI re-runs
 *  ``/readiness`` after this returns to refresh the panel. */
export async function resolvePostMappingFindings(
  sessionId: string,
  resolutions: ReadinessResolutionRequest[],
): Promise<PostMappingResolveResponse> {
  const url = buildUrl(
    `/api/v1/agents/sessions/${encodeURIComponent(sessionId)}/readiness/resolve`,
  );
  const response = await apiClient.post<PostMappingResolveResponse>(url, {
    resolutions,
  });
  return response.data;
}

/** GET /api/v1/mapping/readiness/{batch_id} — latest report + resolved state. */
export async function fetchPreMappingReadiness(batchId: string): Promise<{
  report: ReadinessReportResponse;
  applied_resolutions:
    | ReadinessResolveResponse
    | null;
  blocking_unresolved: boolean;
}> {
  const url = buildUrl(
    `/api/v1/mapping/readiness/${encodeURIComponent(batchId)}`,
  );
  const response = await apiClient.get(url);
  return response.data;
}
